import { gql } from '@apollo/client';
import { DoNotDisturbOn, PauseCircle, Repeat, RepeatOne } from '@mui/icons-material';
import moment from 'moment';
import { tz } from 'moment-timezone';

import {
    IOrderFrequency,
    IOrderPrn,
    isOrderDiscontinued,
    isSingletonFrequency,
} from '../../types/order';
import { ClickEvent } from '../../utils/clickEvent';
import { formatNullableDatetime } from '../../utils/datetimes/formatting';
import { AxleChip } from '../displayFields/chips';
import { renderOrderFrequency1xStyle } from './labels';

export interface IOrderChip {
    id: string;
    externalId: string | null;
    prn: IOrderPrn | null;
    frequency: IOrderFrequency | null;
    discontinuedAt: Date | null;
    isPaused: boolean;
    timezone: string | null;
}

export const OrderChipFragment = gql`
    fragment OrderChipFragment on Order {
        id
        externalId
        discontinuedAt
        isPaused
        timezone
        prn {
            authorizedVisitCount
        }
        frequency {
            intervalCount
            intervalUnit
            numVisitsPerInterval
        }
    }
`;

export const OrderChip = ({
    order: { id, frequency, prn, discontinuedAt, timezone, isPaused },
    link = false,
}: {
    order: IOrderChip;
    link?: boolean;
}) => {
    const isDiscontinued = isOrderDiscontinued(discontinuedAt);
    const tooltip = isDiscontinued
        ? `Discontinued ${formatNullableDatetime(moment(discontinuedAt), timezone ?? tz.guess())}`
        : '';
    const linkUrl = link ? `/orders/${id}` : undefined;
    const onClick = link
        ? (event: ClickEvent) => {
              // Stop propagation so that table row click events don't trigger..
              event.stopPropagation();
          }
        : undefined;
    if (prn) {
        return (
            <AxleChip
                label='PRN Visits'
                tooltip={tooltip}
                numberIcon={isDiscontinued ? undefined : prn.authorizedVisitCount}
                icon={isDiscontinued ? <DoNotDisturbOn /> : undefined}
                routerLink={linkUrl}
                onClick={onClick}
            />
        );
    }
    if (frequency) {
        const label = renderOrderFrequency1xStyle({ frequency, withIntervals: true });
        if (isDiscontinued) {
            return (
                <AxleChip
                    icon={<DoNotDisturbOn />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        }
        if (isPaused) {
            return (
                <AxleChip
                    icon={<PauseCircle />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        }
        if (isSingletonFrequency(frequency)) {
            return (
                <AxleChip
                    icon={<RepeatOne />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        } else {
            return (
                <AxleChip
                    icon={<Repeat />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        }
    }
    return <AxleChip label='Order' tooltip={tooltip} />;
};
